import { useEffect, useMemo, useState } from "react";
import { History } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getSnapshots, type SnapshotRow } from "@/lib/desktop-history";

/**
 * The timeline of a document as this Mac saw it.
 *
 * Snapshots are local — they exist so "I cut a whole section on Thursday and
 * now I want it back" has an answer that works on a plane. Restoring goes
 * through the editor's undoable replace, so even a restore can itself be
 * undone with ⌘Z.
 */

interface VersionHistoryProps {
  documentId: number;
  open: boolean;
  onClose: () => void;
  onRestore: (snapshot: { content: string; title: string }) => void;
}

function formatWhen(epochMs: number): string {
  const date = new Date(epochMs);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  const time = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (sameDay) return `Today at ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return `Yesterday at ${time}`;
  return `${date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  })} at ${time}`;
}

export function VersionHistory({ documentId, open, onClose, onRestore }: VersionHistoryProps) {
  const [snapshots, setSnapshots] = useState<SnapshotRow[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    void getSnapshots(documentId)
      .then((rows) => {
        setSnapshots(rows);
        setSelectedId(rows[0]?.id ?? null);
      })
      .catch(() => setSnapshots([]))
      .finally(() => setLoading(false));
  }, [open, documentId]);

  const selected = useMemo(
    () => snapshots.find((row) => row.id === selectedId) ?? null,
    [snapshots, selectedId],
  );

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Version history
          </DialogTitle>
          <DialogDescription>
            Saved on this Mac as you wrote. Restoring can be undone with ⌘Z.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
        ) : snapshots.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No versions yet. A version is kept each time this document saves,
            so the timeline will build as you write.
          </p>
        ) : (
          <div className="grid grid-cols-[14rem_1fr] gap-3" style={{ height: "24rem" }}>
            <ScrollArea className="h-full rounded-md border border-border">
              <div className="p-1">
                {snapshots.map((row) => (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => setSelectedId(row.id)}
                    className={`block w-full rounded-md px-2.5 py-2 text-left text-sm ${
                      row.id === selectedId
                        ? "bg-accent text-accent-foreground"
                        : "hover:bg-accent/50"
                    }`}
                  >
                    <span className="block truncate font-medium">
                      {formatWhen(row.created_at)}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {row.word_count} words
                    </span>
                  </button>
                ))}
              </div>
            </ScrollArea>

            <div className="flex h-full min-w-0 flex-col gap-2">
              <ScrollArea className="min-h-0 flex-1 rounded-md border border-border">
                <pre className="whitespace-pre-wrap break-words p-3 font-sans text-sm">
                  {selected?.content ?? ""}
                </pre>
              </ScrollArea>
              <div className="flex justify-end">
                <Button
                  size="sm"
                  disabled={!selected}
                  onClick={() => {
                    if (!selected) return;
                    onRestore({ content: selected.content, title: selected.title });
                    onClose();
                  }}
                >
                  Restore this version
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
